'use client';

import React, { useState } from 'react';
import { PulseButton, Chip, Tooltip, Typography } from 'src/components';
import { faSparkles, faList } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import AiPromptDetailsModal from './AiPromptDetailsModal';
import { truncateString } from 'src/utils/truncateString/truncateString';
import { useDrawer } from 'src/hooks/drawers/useDrawer';

interface TablePrimaryHeaderProps {
  subTitle?: string;
  title: string;
  level?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  aiDetails?: {
    criteria: string;
    action_plan?: any[];
  };
  showListUploaderButton?: boolean;
}

const TablePrimaryHeader = ({
  subTitle = '',
  title,
  level = 'h4',
  aiDetails,
  showListUploaderButton = false,
}: TablePrimaryHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAiList = aiDetails?.criteria && aiDetails?.action_plan;
  const { closeAllButCurrent } = useDrawer();

  return (
    <div className="flex flex-col gap-2 justify-center h-full w-full">
      <div className="flex items-center justify-between w-full gap-2">
        <div>
          <div className="flex flex-row items-center gap-2">
            <Typography
              variant={level}
              className="text-[#171A1C]"
            >
              {title}
              {isAiList && (
                <span className="inline-flex align-middle ml-2">
                  <Chip
                    color="primary"
                    variant="solid"
                    size="sm"
                    className="font-semibold"
                    startDecorator={<Icon icon={faSparkles} size="sm" />}
                  >
                    AI Generated
                  </Chip>
                </span>
              )}
            </Typography>
          </div>
          {subTitle && (
            <Typography
              variant="body2"
              className="text-[#555E68]"
            >
              {subTitle}
            </Typography>
          )}
          {isAiList && (
            <div className="flex flex-col gap-1 mt-2">
              <div className="flex flex-row items-center">
                {aiDetails.criteria.length > 220 ? (
                  <Tooltip
                    delay={500}
                    content={aiDetails.criteria}
                    position="bottom"
                  >
                    <Typography variant="caption" color="primary" bold>
                      List Criteria:
                      <Typography variant="caption" color="primary" className="ml-1">
                        {truncateString(aiDetails.criteria, 220)}
                      </Typography>
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" color="primary" bold>
                    List Criteria:
                    <Typography variant="caption" color="primary" className="ml-1">
                      {aiDetails.criteria}
                    </Typography>
                  </Typography>
                )}
              </div>
              <div className="flex flex-row items-center gap-1">
                <span
                  className="text-link font-medium cursor-pointer hover:underline text-xs"
                  onClick={() => setIsModalOpen(true)}
                >
                  View AI Prompt Details
                </span>
              </div>
            </div>
          )}
        </div>
        {showListUploaderButton && (
          <PulseButton
            size="sm"
            variant="outline"
            color="neutral"
            startDecorator={faList}
            onClick={() => {
              closeAllButCurrent('listsUploader');
            }}
            className="w-full max-w-36 rounded-full"
          >
            List Uploader
          </PulseButton>
        )}
      </div>

      <AiPromptDetailsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
        criteria={aiDetails?.criteria}
        actionPlan={aiDetails?.action_plan}
      />
    </div>
  );
};

export default TablePrimaryHeader;
